// Imports
import { SetupIntent, Stripe, StripeError } from '@stripe/stripe-js';
import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import Input from '../../../../../../shareables/foundation/front-end/components/forms/fields/input';
import Radio from '../../../../../../shareables/foundation/front-end/components/forms/fields/radio';
import commonActions from '../../../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import BankAccount from '../../../../../../shareables/front-end/stripe-component-wrappers/bank-account';
import modifyStripeMessage from '../../../../../../shareables/front-end/stripe-component-wrappers/utils/modify-stripe-message';
import BankAccountFields from '../../../../../../shareables/integrations/api/components/fields/bank-account/fields';
import reportStripeError from '../utils/report-stripe-error';


// Define the accepted ref
export interface BankAccountSetupIntentHandlerRef {
	handleSubmit: (clientSecret: string) => Promise<SetupIntent | undefined>;
}


// Define type
type AccountHolderType = React.ComponentProps<typeof BankAccount>['bankAccountDetails']['accountHolderType'];


// Define the accepted props
interface Props {
	stripePromise: Promise<Stripe | null>;
}


// Function component
const BankAccountSetupIntentHandler = forwardRef<BankAccountSetupIntentHandlerRef, Props>(({ stripePromise }, ref) => {
	// Use state
	const [accountHolderName, setAccountHolderName] = useState<string>('');
	const [accountHolderType, setAccountHolderType] = useState<AccountHolderType | undefined>(undefined);
	const [accountNumber, setAccountNumber] = useState<string>('');
	const [routingNumber, setRoutingNumber] = useState<string>('');
	const [accountNumberError, setAccountNumberError] = useState<string>('');
	const [routingNumberError, setRoutingNumberError] = useState<string>('');
	const [stripeError, setStripeError] = useState<string>('');
	const [accountHolderTypeError, setAccountHolderTypeError] = useState<string>('');
	const [accountHolderNameError, setAccountHolderNameError] = useState<string>('');
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const userEmail = useAppSelector((state) => state.common.user.email);
	
	
	// Handle Stripe errors
	const handleError = useCallback(
		(error: StripeError) => {
			// Initialize
			const isRoutingNumberError = error.param === 'payment_method_data[us_bank_account][routing_number]';
			const isAccountNumberError = error.param === 'payment_method_data[us_bank_account][account_number]';
			const isInvalidParameter = Boolean(error.code?.startsWith('payment_method_invalid_parameter'));
			
			
			// Determine if this is an error we want to show to users
			const canShowToUsers =
				error.type === 'validation_error' ||
				error.code === 'bank_account_unusable' ||
				(isInvalidParameter && (isRoutingNumberError || isAccountNumberError));
			
			
			// Handle if it can be shown to users
			if (canShowToUsers) {
				let message: string;
				
				if (error.code === 'payment_method_invalid_parameter' && isRoutingNumberError) {
					message = 'Invalid routing number.';
				} else if (error.code === 'payment_method_invalid_parameter' && isAccountNumberError) {
					message = 'Invalid account number.';
				} else {
					message =
						error.message ??
						'Something went wrong while processing these details. Please double-check the details and try again.';
				}
				
				const setError = isRoutingNumberError
					? setRoutingNumberError
					: isAccountNumberError
						? setAccountNumberError
						: setStripeError;
				
				setError(modifyStripeMessage(message));
				
				return;
			}
			
			
			// Handle all other unexpected errors
			reportStripeError(error);
			
			dispatch(
				commonActions.showPageMessage({
					color: 'danger',
					title: 'Oh snap!',
					message: 'There was an unexpected issue.',
				})
			);
		},
		[dispatch]
	);
	
	
	// Handle submission
	const handleSubmit = useCallback(
		async (clientSecret: string) => {
			// Reset errors
			setAccountNumberError('');
			setRoutingNumberError('');
			setStripeError('');
			setAccountHolderTypeError('');
			setAccountHolderNameError('');
			
			
			// Handle if missing info
			if (!accountHolderType) {
				setAccountHolderTypeError('This is required.');
				return;
			}
			
			if (!accountHolderName.trim()) {
				setAccountHolderNameError('This is required.');
				return;
			}
			
			
			// Resolve promise
			const stripe = await stripePromise;
			
			
			// Handle if Stripe.js didn't load for some reason
			if (!stripe) {
				throw new Error('Stripe.js hasn’t loaded');
			}
			
			
			// Confirm the setup intent using the collected bank details
			const { error, setupIntent } = await stripe.confirmUsBankAccountSetup(clientSecret, {
				payment_method: {
					us_bank_account: {
						routing_number: routingNumber,
						account_number: accountNumber,
						account_holder_type: accountHolderType,
					},
					billing_details: {
						name: accountHolderName,
						email: userEmail,
					},
				},
			});
			
			
			// Handle error
			if (error) {
				handleError(error);
			}
			
			
			// Return
			return setupIntent;
		},
		[stripePromise, routingNumber, accountNumber, handleError, userEmail, accountHolderType, accountHolderName]
	);
	
	
	// Expose the method to parent
	useImperativeHandle(ref, () => ({ handleSubmit }), [handleSubmit]);
	
	
	// Build props
	const fieldsProps = {
		name: 'bankAccount',
		bankAccountDetails: {
			countryCode: 'US',
			currencyCode: 'USD',
			accountHolderType: accountHolderType ?? 'individual',
			accountHolderName: accountHolderName,
		},
		accountNumberValue: accountNumber,
		routingNumberValue: routingNumber,
		handleAccountNumberChange: (event: React.ChangeEvent<HTMLInputElement>) => setAccountNumber(event.target.value),
		handleRoutingNumberChange: (event: React.ChangeEvent<HTMLInputElement>) => setRoutingNumber(event.target.value),
		accountNumberError: accountNumberError,
		routingNumberError: routingNumberError,
		stripeError: stripeError,
	};
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Radio
				choices={[
					{
						label: 'Business',
						value: 'company',
					},
					{
						label: 'Individual',
						value: 'individual',
					},
				]}
				name='accountHolderType'
				label='Account type'
				onChange={(newValue) => setAccountHolderType(newValue as AccountHolderType)}
				exclude
				required
				errorMessage={accountHolderTypeError}
			/>
			
			<Input
				type='text'
				name='accountHolderName'
				label='Account holder’s name'
				onChange={(newValue) => setAccountHolderName(newValue)}
				exclude
				required
				errorMessage={accountHolderNameError}
			/>
			
			<BankAccountFields {...fieldsProps} />
		</React.Fragment>
	);
});


// @ts-ignore: displayName does exist
BankAccountSetupIntentHandler.displayName = 'BankAccountSetupIntentHandler';

export default BankAccountSetupIntentHandler;
